/**
 * `LoadingSkeleton` — the calm skeleton rendered while the primary
 * readiness sources are still in flight. It replaces the hero + goal
 * cards + journey + check sections with a monochrome ladder so the
 * page never flashes "Nothing to fix right now." before anything has
 * been measured.
 *
 * No lime, no coral, no amber. Just a ladder of surfaces that match
 * the final layout's rhythm.
 */

import { useC } from '../../lib/theme.js';
import { Kicker } from './MonoLink.js';
import { sansFont } from './fonts.js';

export function LoadingSkeleton(): JSX.Element {
  const C = useC();
  const card = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
  } as const;
  const bar = (w: number) => ({
    height: 10,
    width: w,
    background: C.surface2,
    borderRadius: 2,
  }) as const;
  return (
    <div
      aria-busy="true"
      aria-label="Loading readiness"
      className="flex flex-col"
      style={{ gap: 32, fontFamily: sansFont }}
    >
      <section style={{ ...card, padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Kicker>Checking</Kicker>
        <div style={bar(360)} />
        <div style={bar(240)} />
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ ...card, padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Kicker>&nbsp;</Kicker>
            <div style={{ ...bar(80), height: 4, borderRadius: 2 }} />
            <div style={bar(180)} />
          </div>
        ))}
      </section>

      <section style={{ ...card, padding: '26px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={bar(260)} />
        <div className="grid grid-cols-1 md:grid-cols-4" style={{ gap: 20 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={bar(60)} />
              <div style={bar(44)} />
            </div>
          ))}
        </div>
      </section>

      {[0, 1].map((i) => (
        <section key={i} style={{ ...card }}>
          <div style={{ padding: '24px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={bar(140)} />
            <div style={bar(90)} />
          </div>
          {[0, 1, 2].map((j) => (
            <div
              key={j}
              style={{
                borderTop: `1px solid ${C.borderSoft}`,
                padding: '18px 24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={bar(300)} />
              <div style={bar(220)} />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
